import { compact, isEmpty, sortBy, uniqBy } from 'lodash';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useBoolean, useCurrentUser, useSearchUser } from 'hooks';
import { User } from 'types';

/**
 * Generic hook for managing teammate selection (CC, BCC, etc.)
 * Fetches available teammates from two team sources and manages selection state.
 *
 * @param initSelectedEmails - Initial email addresses to pre-select
 * @param submissionTeamId - Team ID for submission team (client team)
 * @param assigneeTeamId - Team ID for assignee team (Flow team)
 * @returns Available teammates, selected teammates, and toggle function
 */
export function useTeammateSelection(
  initSelectedEmails?: string[],
  submissionTeamId?: string,
  assigneeTeamId?: string,
) {
  const { me } = useCurrentUser();
  const initSelectedTeammates = useMemo(() => initSelectedEmails || [], [initSelectedEmails]);
  const [selectedTeammates, setSelectedTeammates] = useState<User[]>([]);
  const [availableTeammates, setAvailableTeammates] = useState<User[]>([]);
  const [isSelectedTeammatesInitialized, { on: setSelectedTeammatesInitialized }] = useBoolean(false);

  const [userTeamUsers, setUserTeamUsers] = useState<User[] | undefined>(undefined);
  const [leadAssigneeTeamUsers, setLeadAssigneeTeamUsers] = useState<User[] | undefined>(undefined);

  const getPreSelectedTeammates = useCallback(
    (teammateOptions: User[]) => {
      const preSelected = uniqBy(
        compact(initSelectedTeammates.map((email) => teammateOptions.find((user) => user.email === email))),
        'id',
      );
      return isEmpty(preSelected) ? [] : preSelected;
    },
    [initSelectedTeammates],
  );

  const initializeState = useCallback(() => {
    if (me && userTeamUsers !== undefined && leadAssigneeTeamUsers !== undefined) {
      const teammateOptions = uniqBy([...userTeamUsers, ...leadAssigneeTeamUsers], 'id');
      setAvailableTeammates(sortBy(teammateOptions, (user) => (user.id === me.id ? 0 : 1)));

      if (!isSelectedTeammatesInitialized) {
        const preSelectedTeammates = getPreSelectedTeammates(teammateOptions);
        setSelectedTeammates(preSelectedTeammates);
        setSelectedTeammatesInitialized();
      }
    }
  }, [
    me,
    userTeamUsers,
    leadAssigneeTeamUsers,
    isSelectedTeammatesInitialized,
    getPreSelectedTeammates,
    setSelectedTeammatesInitialized,
  ]);

  useSearchUser({
    filter: {
      teamId: assigneeTeamId,
      enabled: true,
    },
    enabled: !!assigneeTeamId,
    queryOptions: {
      onSuccess: (data) => {
        setLeadAssigneeTeamUsers(data?.items || []);
      },
    },
  });

  useSearchUser({
    filter: { teamId: submissionTeamId, enabled: true },
    enabled: !!submissionTeamId,
    queryOptions: {
      onSuccess: (data) => {
        setUserTeamUsers(data?.items || []);
      },
    },
  });

  // Initialize state whenever either set of users changes
  useEffect(() => {
    initializeState();
    // We only want to initialize the state when 'me' or the user team users or lead assignee team users change.
    // Alternative is to add useCallback to both getPreSelectedTeammates and initializeState.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userTeamUsers, leadAssigneeTeamUsers, me]);

  const toggleTeammateSelection = (user: User) => {
    if (selectedTeammates.findIndex((teammate) => teammate.id === user.id) === -1) {
      setSelectedTeammates((prev) => [...prev, user]);
    } else {
      setSelectedTeammates((prev) => prev.filter((item) => item.id !== user.id));
    }
  };

  return {
    availableTeammates,
    selectedTeammates,
    toggleTeammateSelection,
  };
}
